import React, { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, ShieldAlert, Clock, CheckCircle2, XCircle, RefreshCw, Sparkles, MessageSquareWarning, Scissors } from "lucide-react";
import { toast } from "sonner";
import { GroomingFeedbackDialog } from "@/components/app/GroomingFeedbackDialog";

const STATUS_TABS = [
  { key: "failed", label: "Flagged" },
  { key: "pending", label: "Analyzing" },
  { key: "passed", label: "Passed" },
  { key: "all", label: "All" },
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

function VerdictChip({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded px-1.5 py-0.5 ${ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {label}
    </span>
  );
}

function PhotoCell({ path, label }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">{label}</div>
      {path ? (
        <a href={`${API}/files?path=${encodeURIComponent(path)}`} target="_blank" rel="noreferrer">
          <img src={`${API}/files?path=${encodeURIComponent(path)}`} alt={label} className="w-full h-28 object-cover rounded border border-zinc-200" />
        </a>
      ) : (
        <div className="w-full h-28 rounded border border-dashed border-zinc-200 flex items-center justify-center text-[10px] text-zinc-300">No photo</div>
      )}
    </div>
  );
}

export default function GroomingChecks() {
  const { selectedId } = useProperty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("failed");
  const [actingId, setActingId] = useState(null);
  const [feedbackFor, setFeedbackFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("status", tab);
      if (selectedId) params.set("property_id", selectedId);
      const { data: d } = await api.get(`/grooming-checks?${params.toString()}`);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load grooming checks");
    } finally {
      setLoading(false);
    }
  }, [tab, selectedId]);

  useEffect(() => { load(); }, [load]);

  const override = async (tid, action) => {
    setActingId(tid + action);
    try {
      await api.post(`/grooming-checks/${tid}/override`, { action });
      toast.success(action === "dismiss" ? "Warning revoked" : "Warning upheld");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setActingId(null);
    }
  };

  const s = data?.summary;

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="label-overline">Conduct</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Grooming Checks</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            AI-reviewed shift-start photos (face, uniform, shoes). <strong>Facial-hair policy:</strong> only a moustache and/or a french beard (goatee) are allowed — any beard on the cheeks, jaw or neck is flagged. Disagree with a verdict? Hit <em>Correct AI</em> so the system learns; you can also grant a groomed-beard exception to a specific employee.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="grooming-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { k: "total", label: "Total", val: s.total, cls: "bg-white" },
            { k: "failed", label: "Flagged", val: s.failed, cls: s.failed > 0 ? "bg-red-50 border-red-200 text-red-900" : "bg-white" },
            { k: "pending", label: "Analyzing", val: s.pending, cls: "bg-white" },
            { k: "passed", label: "Passed", val: s.passed, cls: "bg-emerald-50 border-emerald-200 text-emerald-900" },
          ].map((c) => (
            <div key={c.k} className={`border border-zinc-200 rounded-md p-3 ${c.cls}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{c.label}</div>
              <div className="text-2xl font-bold font-display mt-0.5">{c.val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1 bg-white border border-zinc-200 rounded-md p-1 w-fit" data-testid="grooming-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-sm rounded ${tab === t.key ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            data-testid={`grooming-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : !data?.checks?.length ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-md py-16 text-center text-zinc-400">No grooming checks in this view.</div>
      ) : (
        <div className="space-y-3">
          {data.checks.map((c) => {
            const gc = c.grooming_check || {};
            const photos = c.grooming_photos || {};
            const failed = gc.status === "failed";
            const reviewed = gc.review_status === "dismissed" || gc.review_status === "upheld";
            return (
              <section key={c.id} className={`bg-white rounded-md border p-4 ${failed && !reviewed ? "border-red-300" : "border-zinc-200"}`} data-testid={`grooming-check-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                      {c.user_name}
                      {c.facial_hair_exempt && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`grooming-beard-exempt-${c.id}`}>
                          <Scissors className="w-3 h-3" /> Beard allowed
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
                      <span className="capitalize">{(c.type || "").replace("_", " ")}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(c.timestamp)}</span>
                      <span className="uppercase tracking-widest text-[10px] bg-zinc-100 rounded px-1.5 py-0.5">{c.source}</span>
                    </div>
                  </div>
                  <div>
                    {gc.status === "pending" && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-zinc-100 text-zinc-600 rounded px-2 py-1"><Loader2 className="w-3 h-3 animate-spin" /> Analyzing</span>}
                    {gc.status === "passed" && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-2 py-1"><ShieldCheck className="w-3 h-3" /> Passed</span>}
                    {gc.status === "failed" && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 rounded px-2 py-1"><ShieldAlert className="w-3 h-3" /> Flagged</span>}
                    {gc.status === "error" && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-1">Review manually</span>}
                  </div>
                </div>

                <div className="flex gap-2 mt-3">
                  <PhotoCell path={photos.face} label="Face" />
                  <PhotoCell path={photos.uniform} label="Uniform" />
                  <PhotoCell path={photos.shoes} label="Shoes" />
                </div>

                {(gc.status === "passed" || gc.status === "failed") && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <VerdictChip ok={gc.clean_shaven} label="Facial hair" />
                    <VerdictChip ok={gc.wearing_shoes} label="Shoes" />
                    <VerdictChip ok={gc.proper_uniform} label="Uniform" />
                  </div>
                )}
                {gc.notes && <p className="text-xs text-zinc-600 mt-2 italic">AI: {gc.notes}</p>}
                {gc.issues?.length > 0 && (
                  <ul className="text-xs text-red-700 mt-1 list-disc list-inside">
                    {gc.issues.map((it, i) => <li key={i}>{it}</li>)}
                  </ul>
                )}

                {(gc.status === "passed" || gc.status === "failed") && (
                  <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs">
                      {gc.review_status === "corrected" ? (
                        <span className="text-zinc-600 font-semibold inline-flex items-center gap-1" data-testid={`grooming-corrected-${c.id}`}>
                          <MessageSquareWarning className="w-3 h-3" /> Corrected by {gc.reviewed_by_name || "manager"}
                        </span>
                      ) : reviewed ? (
                        <span className={`font-semibold ${gc.review_status === "dismissed" ? "text-zinc-500" : "text-red-700"}`} data-testid={`grooming-reviewed-${c.id}`}>
                          {gc.review_status === "dismissed" ? "Warning revoked" : "Warning upheld"}
                          {gc.reviewed_by_name ? ` by ${gc.reviewed_by_name}` : ""}
                        </span>
                      ) : failed ? (
                        <span className="text-zinc-500 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> A soft warning was auto-issued.</span>
                      ) : (
                        <span className="text-emerald-700 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> AI marked this compliant.</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setFeedbackFor(c)} data-testid={`grooming-correct-${c.id}`}>
                        <MessageSquareWarning className="w-3.5 h-3.5 mr-1" /> Correct AI
                      </Button>
                      {failed && !reviewed && (
                        <Button size="sm" variant="outline" disabled={actingId === c.id + "dismiss"} onClick={() => override(c.id, "dismiss")} data-testid={`grooming-dismiss-${c.id}`}>
                          {actingId === c.id + "dismiss" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Revoke warning
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <GroomingFeedbackDialog
        open={!!feedbackFor}
        onOpenChange={(o) => { if (!o) setFeedbackFor(null); }}
        check={feedbackFor}
        onSubmitted={load}
      />
    </div>
  );
}
